/**
 * Workspace accent color helpers.
 * Stored as #rrggbb; UI tints via CSS color-mix(--ws-color, ...) so any valid hex works.
 */

export const WORKSPACE_COLOR_PRESETS = [
  { value: '#3b82f6', label: 'Blue' },
  { value: '#22c55e', label: 'Green' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#ef4444', label: 'Red' },
  { value: '#a855f7', label: 'Purple' },
  { value: '#14b8a6', label: 'Teal' },
  { value: '#64748b', label: 'Slate' },
  { value: '#f43f5e', label: 'Rose' },
] as const

export const DEFAULT_WORKSPACE_COLOR = WORKSPACE_COLOR_PRESETS[0].value

/** Normalize free-form input to lowercase #rrggbb, or null if invalid. */
export function normalizeWorkspaceHex(input: string): string | null {
  const raw = input.trim()
  if (!raw) return null
  const withHash = raw.startsWith('#') ? raw : `#${raw}`
  if (/^#[0-9a-fA-F]{6}$/.test(withHash)) return withHash.toLowerCase()
  if (/^#[0-9a-fA-F]{3}$/.test(withHash)) {
    const r = withHash[1]
    const g = withHash[2]
    const b = withHash[3]
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase()
  }
  return null
}

export function isWorkspaceColorPreset(hex: string | null | undefined): boolean {
  const n = hex ? normalizeWorkspaceHex(hex) : null
  if (!n) return false
  return WORKSPACE_COLOR_PRESETS.some((p) => p.value === n)
}

/**
 * True-hue translucent fill: #rrggbb + alpha byte.
 * Prefer this over color-mix(..., grey) which greys out custom colors on light mode.
 */
export function workspaceColorWithAlpha(
  hex: string | null | undefined,
  alpha01: number,
): string | undefined {
  const n = hex ? normalizeWorkspaceHex(hex) : null
  if (!n) return undefined
  const a = Math.round(Math.max(0, Math.min(1, alpha01)) * 255)
  return `${n}${a.toString(16).padStart(2, '0')}`
}

/** Solid accent fill for icon tiles / badges (pair with white glyph). */
export function workspaceAccentFill(hex: string | null | undefined): string | undefined {
  return hex ? normalizeWorkspaceHex(hex) ?? undefined : undefined
}

/** Glyph / initials on a solid accent tile. */
export const WS_ICON_ON_ACCENT = '#ffffff'

/** Soft chip wash when the glyph stays the accent color (not on a solid tile). */
export const WS_CHIP_FILL_ALPHA = 0.22

